import { useEffect, useState, useCallback } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useNavigate } from "react-router-dom";
import {
	Euro,
	HandCoins,
	HeartPulse,
	Home as HomeIcon,
	ShoppingBag,
	Scale,
	User,
	Utensils,
	FileText,
	CircleAlert,
	ArrowRight,
	RefreshCw,
} from "lucide-react";
import { useTranslation } from "../i18n/i18n";
import StickyTitle from "../components/StickyTitle/StickyTitle";
import Loader from "../components/Loader/Loader";
import SliderWrapper from "../components/SliderWrapper/SliderWrapper";
import EmptyState from "../components/EmptyState/EmptyState";
import useLocale from "../hooks/useLocale";
import useSyncUserEmailStatus from "../hooks/useSyncUserEmailStatus";
import { useRatios } from "../hooks/useRatios";
import { formatCurrency } from "../utils/format";
import { useUserPreferences } from "../contexts/UserPreferencesContext";

const Home = () => {
	const { locale } = useLocale();
	const language = locale.startsWith("fr") ? "fr" : "en";
	const { translate } = useTranslation();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [stats, setStats] = useState(null);
	const [activeYear, setActiveYear] = useState(null);
	const [error, setError] = useState(null);
	const { currentUser, authInitialized } = useUserPreferences();

	// React Query Hook for Ratios
	const currentYear = new Date().getFullYear();
	const currentMonth = `${currentYear}-${String(
		new Date().getMonth() + 1
	).padStart(2, "0")}`;

	const {
		data: ratiosData,
		isLoading: ratiosLoading,
		error: ratiosError,
		refetch: refetchRatios,
	} = useRatios({
		year: currentYear,
		yearMonth: currentMonth,
		enabled: authInitialized && !!currentUser,
	});

	// Sync current user's email with Firestore (if needed)
	useSyncUserEmailStatus(currentUser, authInitialized);

	// Navigation handler
	const handleNavigateToAdd = useCallback(() => {
		navigate("/add-transaction");
	}, [navigate]);

	// Retry handler
	const handleRetry = useCallback(() => {
		loadStats();
		refetchRatios();
	}, [refetchRatios]);

	const loadStats = async () => {
		try {
			setLoading(true);
			setError(null);
			const getStats = httpsCallable(functions, "getStatsFunction");

			// Retrieve basic stats
			const statsResult = await getStats();
			setStats(statsResult.data);
		} catch (err) {
			console.error("❌ Error loading stats:", err);
			setError(err?.message || "Unknown error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadStats();
	}, []);

	// Managing ratio errors
	useEffect(() => {
		if (ratiosError) {
			console.error("❌ Error loading ratios:", ratiosError);
			setError(ratiosError?.message || "Ratios loading error");
		}
	}, [ratiosError]);

	// Check if there's any data
	const hasAnyData =
		stats &&
		(stats.allTime?.totalIncome > 0 ||
			stats.allTime?.totalExpenses > 0 ||
			Object.keys(stats.yearly || {}).length > 0);

	const buildCards = data => [
		{
			label: translate("home.totalIncome"),
			value: data.totalIncome || 0,
			icon: <Euro size={20} />,
			type: "income",
		},
		{
			label: translate("home.totalSpent"),
			value: data.totalExpenses || 0,
			icon: <HandCoins size={20} />,
			type: "expense",
		},
		{
			label: translate("home.balance"),
			value: data.balance || 0,
			icon: <Scale size={20} />,
			type: "balance",
		},
		{
			label: translate("category.housing"),
			value: data.housing || 0,
			icon: <HomeIcon size={20} />,
			type: "expense",
		},
		{
			label: translate("category.food"),
			value: data.food || 0,
			icon: <Utensils size={20} />,
			type: "expense",
		},
		{
			label: translate("category.general"),
			value: data.general || 0,
			icon: <FileText size={20} />,
			type: "expense",
		},
		{
			label: translate("category.health"),
			value: data.health || 0,
			icon: <HeartPulse size={20} />,
			type: "expense",
		},
		{
			label: translate("category.personal"),
			value: data.personal || 0,
			icon: <User size={20} />,
			type: "expense",
		},
		{
			label: translate("home.totalWithoutHousing"),
			value: data.totalHorsLogement || 0,
			icon: <ShoppingBag size={20} />,
			type: "expense",
		},
	];

	// Improved function to manage ratios with NaN handling
	const getPercent = (value, total) => {
		// Check if the values are valid
		if (!value || !total || total === 0 || isNaN(value) || isNaN(total)) {
			return "–";
		}

		const percentage = Math.round((value / total) * 100);

		// Check if the result is NaN
		if (isNaN(percentage)) {
			return "–";
		}

		return `${percentage}%`;
	};

	const getCategoryClass = (label, type) => {
		if (type === "income" && label === translate("home.totalIncome")) {
			return "category-income";
		}
		const mapping = {
			[translate("category.housing")]: "housing",
			[translate("category.food")]: "food",
			[translate("category.general")]: "general",
			[translate("category.health")]: "health",
			[translate("category.personal")]: "personal",
		};
		const id = mapping[label];
		return id ? `category-${id}` : "";
	};

	// Error state
	if (error) {
		return (
			<div className="content">
				<StickyTitle title={translate("home.title")} />
				<div className="page-content">
					<EmptyState
						icon={CircleAlert}
						title={translate("error.title")}
						description={`${translate("error.message")} ${error}`}
						variant="error"
						actionButton={{
							text: translate("error.retry"),
							onClick: handleRetry,
							icon: RefreshCw,
						}}
					/>
				</div>
			</div>
		);
	}

	// Loading state
	if (loading || !stats || ratiosLoading) {
		return (
			<div className="content">
				<StickyTitle title={translate("home.title")} />
				<div className="page-content">
					<Loader
						fullscreen={true}
						loadingTextKey="home.loading"
						theme="light"
						translate={translate}
					/>
				</div>
			</div>
		);
	}

	// Empty state - no data available
	if (!hasAnyData) {
		return (
			<div className="content">
				<StickyTitle title={translate("home.title")} />
				<div className="page-content">
					<EmptyState
						icon={Euro}
						title={translate("home.empty.title")}
						description={translate("home.empty.description")}
						actionButton={{
							text: translate("form.add"),
							onClick: handleNavigateToAdd,
							icon: ArrowRight,
						}}
					/>
				</div>
			</div>
		);
	}

	// Combine stats and ratios
	const combinedData = {
		...stats,
		ratios: ratiosData || {},
	};
	console.log("📊 Combined data with ratios:", combinedData);

	// Main content with data
	const allTimeStats = stats.allTime;
	const yearlyStats = stats.yearly;
	const monthlyStats = stats.monthly;

	const allYearSlides = [
		{
			label: translate("home.totalAllTime"),
			rows: buildCards(allTimeStats),
			stats: {
				totalIncome: allTimeStats.totalIncome || 0,
				totalExpenses: allTimeStats.totalExpenses || 0,
			},
			year: "all",
		},
		...Object.entries(yearlyStats)
			.sort((a, b) => b[0] - a[0])
			.map(([year, yearData]) => ({
				label: year,
				rows: buildCards(yearData),
				stats: {
					totalIncome: yearData.totalIncome || 0,
					totalExpenses: yearData.totalExpenses || 0,
				},
				year: Number(year),
			})),
	];

	const currentYearIndex = allYearSlides.findIndex(
		s => Number(s.year) === currentYear
	);
	const validInitialIndex = currentYearIndex > 0 ? currentYearIndex : 0;

	const currentMonthSlides =
		activeYear && monthlyStats[activeYear]
			? Object.entries(monthlyStats[activeYear])
					.sort((a, b) => b[0] - a[0])
					.map(([month, monthData]) => ({
						label: new Date(activeYear, month).toLocaleString(locale, {
							month: "long",
						}),
						rows: buildCards(monthData),
						stats: {
							totalIncome: monthData.totalIncome || 0,
							totalExpenses: monthData.totalExpenses || 0,
						},
					}))
			: [];

	return (
		<div className="content">
			<StickyTitle title={translate("home.title")} />
			<div className="page-content">
				<SliderWrapper
					slides={allYearSlides}
					initialIndex={validInitialIndex}
					onActiveIndexChange={index => {
						const slide = allYearSlides[index];
						setActiveYear(slide?.year !== "all" ? Number(slide.year) : null);
					}}
				>
					{(row, index, totalExpenses, totalIncome) => {
						const percentOfExpenses = getPercent(row.value, totalExpenses);
						const percentOfIncome = getPercent(row.value, totalIncome);
						const balancePercent = getPercent(row.value, totalIncome);

						return (
							<div
								key={index}
								className={`entry-card home-card ${getCategoryClass(
									row.label,
									row.type
								)}`}
							>
								<div className="home-card-title">
									{row.icon}
									<span>{row.label}</span>
								</div>
								<div className="home-card-stats">
									<div className="home-card-amount">
										{formatCurrency(row.value, language)}
									</div>
									{row.type === "expense" && (
										<div className="ratios">
											<div>
												{percentOfExpenses} / {percentOfIncome}
											</div>
											<div className="ratios-label">
												{translate("home.ofExpenses")} /{" "}
												{translate("home.ofIncome")}
											</div>
										</div>
									)}
									{row.type === "balance" && (
										<div>
											<div className="ratios">{balancePercent}</div>
											<div className="ratios-label">
												{translate("home.ofIncome")}
											</div>
										</div>
									)}
								</div>
							</div>
						);
					}}
				</SliderWrapper>

				{activeYear && currentMonthSlides.length > 0 && (
					<div className="monthly-slider">
						<SliderWrapper slides={currentMonthSlides}>
							{(row, index, totalExpenses, totalIncome) => {
								const percentOfExpenses = getPercent(row.value, totalExpenses);
								const percentOfIncome = getPercent(row.value, totalIncome);
								const balancePercent = getPercent(row.value, totalIncome);

								return (
									<div
										key={index}
										className={`entry-card home-card ${getCategoryClass(
											row.label,
											row.type
										)}`}
									>
										<div className="home-card-title">
											{row.icon}
											<span>{row.label}</span>
										</div>
										<div className="home-card-stats">
											<div className="home-card-amount">
												{formatCurrency(row.value, language)}
											</div>
											{row.type === "expense" && (
												<div className="ratios">
													<div>
														{percentOfExpenses} / {percentOfIncome}
													</div>
													<div className="ratios-label">
														{translate("home.ofExpenses")} /{" "}
														{translate("home.ofIncome")}
													</div>
												</div>
											)}
											{row.type === "balance" && (
												<div>
													<div className="ratios">{balancePercent}</div>
													<div className="ratios-label">
														{translate("home.ofIncome")}
													</div>
												</div>
											)}
										</div>
									</div>
								);
							}}
						</SliderWrapper>
					</div>
				)}
			</div>
		</div>
	);
};

export default Home;
